import {
  EMR_PAGE_SOURCE_LABELS,
  type EmrPageSource,
  type SchedulingChartMatch,
  type SchedulingChartMatchSignal,
  type SchedulingPatientMatches,
  type WalkInChartMatchesRequest,
  type WalkInPatientMatch,
  WalkInQuickRegistrationSchema,
  type WalkInUrgency,
  walkInChartMatchesRequest,
  walkInQuickCreateInput,
} from "@emr/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { createAgentClient } from "../api/agentClient";
import {
  StartPatientEncounterInputSchema,
  type StartPatientEncounterResult,
  type WalkInIntakeInput,
} from "../api/clinicalWorkspace";
import { schedulingErrorDetail } from "../demo/schedulingErrors";
import { EmrImportPanel, type ReadChartPage } from "../emr-import/EmrImportPanel";
import {
  EmrImportBanner,
  EmrImportChartReview,
  EmrImportLeftOut,
} from "../emr-import/EmrImportReview";
import {
  chartImportInput,
  type ExtractedAddress,
  type ExtractedEmergencyContact,
  keptCount,
  keptFiles,
  registrationPrefill,
  reviewItems,
} from "../emr-import/extraction";
import { fileImportedFiles } from "../emr-import/importedFiles";
import type { PreparedHealthCardPhoto } from "../lib/healthCardPhoto";
import { newProfileIdempotencyKey } from "../pages/patient-profile/usePatientProfile";
import {
  type BookingMessagingAnswer,
  BookingMessagingField,
} from "../pages/schedule/MessagingConsentPanel";
import { messagingDecisionRefusalText } from "../pages/schedule/patientMessageCopy";
import { ScheduleModal } from "../pages/schedule/ScheduleModal";
import "../patient-profile.css";
import { WalkInForm, type WalkInHealthCardPhotos } from "./WalkInForm";
import { WalkInIntakeCode } from "./WalkInIntakeCode";

type AgentClient = ReturnType<typeof createAgentClient>;

/**
 * The routes the dialog uses. Phone codes also need createWalkIn and getWalkInChartMatches;
 * recording what the patient said about texts and emails (PA-8) needs the clinic's notice and
 * the chart's decision route.
 */
export type WalkInDialogClient = Pick<
  AgentClient,
  "searchWalkInPatients" | "startPatientEncounter"
> &
  Partial<
    Pick<
      AgentClient,
      | "createWalkIn"
      | "getWalkInChartMatches"
      | "getPatientConsentDocuments"
      | "recordPatientMessagingDecision"
    >
  >;

/** What became of the walk-in's answer about texts and emails, shown with the code. */
interface MessagingOutcome {
  readonly recorded: boolean;
  readonly text: string;
}

/** Set while the clinic can send the questionnaire to the patient's phone (PA-5). */
export interface WalkInPhoneCodes {
  /** The visit is on Today. The dialog stays open on its code; refresh the board. */
  readonly onVisitAdded: (result: StartPatientEncounterResult) => void;
  /** Opens the answers' review once they are in. */
  readonly onReviewIntake?: ((schedulingId: string) => void) | undefined;
}

/**
 * Adding a walk-in from another EMR's chart page (EXECDEV-227): the dialog
 * registers the walk-in itself, then saves the kept chart entries and the
 * address and emergency contact the walk-in intake has no fields for.
 */
export interface WalkInEmrImport {
  readonly source: EmrPageSource;
  readonly client: Pick<
    AgentClient,
    | "createWalkIn"
    | "importEmrPageChart"
    | "fileMigratedDocument"
    | "getPatientProfile"
    | "updatePatientDemographics"
  >;
}

const CONTACT_KEYS = { address: "contact:address", emergencyContact: "contact:emergencyContact" };

function addressLine(address: ExtractedAddress): string {
  return [
    address.line1,
    address.line2,
    address.city,
    address.region,
    address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function contactLine(contact: ExtractedEmergencyContact): string {
  return [contact.name, contact.relationship, contact.phone].filter(Boolean).join(" · ");
}

/**
 * Whether the chart already says what would be written: each field given, compared without
 * case or spacing (the profile may store a province as `province` rather than `region`).
 */
function alreadyHolds(
  current: unknown,
  wanted: Readonly<Record<string, string | undefined>>,
): boolean {
  if (typeof current !== "object" || current === null) return false;
  const stored = current as Readonly<Record<string, unknown>>;
  const comparable = (value: unknown) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";
  return Object.entries(wanted).every(([key, value]) => {
    if (value === undefined) return true;
    const held =
      key === "region" ? (stored.region ?? stored.province ?? stored.state) : stored[key];
    return comparable(held) === comparable(value);
  });
}

const SEX_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
  { value: "unknown", label: "Prefer not to say" },
] as const;

const MATCH_SIGNALS: Record<SchedulingChartMatchSignal, string> = {
  name_and_dob: "name and date of birth",
  health_number: "health number",
  dob_and_phone: "date of birth and phone",
};

/** Waits this long after typing stops before checking for an existing chart (an audited read). */
const MATCH_CHECK_DELAY_MS = 400;

function matchesKey(siteId: string, request: WalkInChartMatchesRequest | null) {
  return ["walk-in-chart-matches", siteId, request === null ? null : JSON.stringify(request)];
}

/** A stable retry key per request body: a retry of the same click reuses it. */
function useRetryKey() {
  const current = useRef({ fingerprint: "", key: crypto.randomUUID() });
  return (fingerprint: string) => {
    if (current.current.fingerprint !== fingerprint) {
      current.current = { fingerprint, key: crypto.randomUUID() };
    }
    return current.current.key;
  };
}

/**
 * Today's **Add walk-in** (an Ice Blue ScheduleModal).
 *
 * - **Add existing patient** searches charts by name, date of birth, health card or phone.
 * - With phone codes (PA-5 / EXECDEV-190): a quick path asks only legal name, date of birth,
 *   sex, an optional mobile number, urgency and the reason. Once name and date of birth are in,
 *   it checks for an existing chart (PA-6's one matcher) and lists any inline, to pick and
 *   reuse; a new chart is made only when none is listed or the desk says none of them is this
 *   patient. **Add and show code** (or Enter in the reason) adds the visit and shows its
 *   questionnaire code in place. **Enter all details now** opens the full registration form.
 * - Without phone codes the dialog is the existing-patient search plus the full registration
 *   form, as before.
 * - With `emrImport` (EXECDEV-227), **Import from …** (in the full form, or from the quick path,
 *   which opens the full form) fills the registration form from another EMR's chart page and
 *   lists its chart entries to keep. **Register and add to Today** then registers the walk-in
 *   from the dialog itself and saves the kept entries, files, address and emergency contact
 *   onto the new chart; if those fail, the walk-in stays on Today and only they are saved again.
 *
 * The embedded registration form (WalkInForm, shared with the Patients page) and the import
 * pieces are Ice Blue `pp-*` markup inside a `.pp-section` (patient-profile.css carries the
 * `--pp-*` tokens and field styles there), as Review intake embeds the chart's editors. With
 * `healthCardPhotos` the form offers a photo of the front of a provincial health card (PA-13),
 * handed to `onSubmit` for the page to send once the chart exists. While an import is under
 * review the dialog registers the walk-in itself and has no photo route, so no photo is offered.
 */
export function WalkInDialog({
  client,
  siteId,
  submitting,
  error,
  onSubmit,
  onAdded,
  onClose,
  codes,
  healthCardPhotos,
  emrImport,
}: {
  client: WalkInDialogClient;
  siteId: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (input: WalkInIntakeInput, photo?: PreparedHealthCardPhoto) => void;
  onAdded: (result: StartPatientEncounterResult) => void;
  onClose: () => void;
  codes?: WalkInPhoneCodes | undefined;
  healthCardPhotos?: WalkInHealthCardPhotos | undefined;
  emrImport?: WalkInEmrImport | undefined;
}) {
  const queryClient = useQueryClient();
  const { createWalkIn, getWalkInChartMatches } = client;
  const { getPatientConsentDocuments, recordPatientMessagingDecision } = client;
  const quickAvailable =
    codes !== undefined && createWalkIn !== undefined && getWalkInChartMatches !== undefined;
  const [messagingAnswer, setMessagingAnswer] = useState<BookingMessagingAnswer | null>(null);
  const [messagingOutcome, setMessagingOutcome] = useState<MessagingOutcome | null>(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [patient, setPatient] = useState<WalkInPatientMatch | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [urgency, setUrgency] = useState<WalkInUrgency>("routine");
  const [entry, setEntry] = useState<"quick" | "full">(quickAvailable ? "quick" : "full");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [sex, setSex] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [picked, setPicked] = useState<SchedulingChartMatch | null>(null);
  const [noneOfThese, setNoneOfThese] = useState(false);
  const [added, setAdded] = useState<{
    readonly encounterId: string;
    readonly displayName: string;
    readonly created: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lock = useRef(false);
  const visitKey = useRetryKey();
  const walkInKey = useRetryKey();
  const listId = useId();
  const newPatientFormId = useId();
  const existingPatientFormId = useId();
  const quickFormId = useId();
  const matchesName = useId();
  const matchesRef = useRef<HTMLDivElement>(null);
  const matchesTitleId = useId();
  const [focusMatches, setFocusMatches] = useState(false);

  // Importing the new patient from another EMR's chart page (into the full registration form).
  const [importStage, setImportStage] = useState<"off" | "waiting" | "review">("off");
  const [imported, setImported] = useState<ReadChartPage | null>(null);
  const [kept, setKept] = useState<ReadonlySet<string>>(() => new Set());
  const [formVersion, setFormVersion] = useState(0);
  /** Registered and on Today, with its imported entries still to save. */
  const [registeredWalkIn, setRegisteredWalkIn] = useState<StartPatientEncounterResult | null>(
    null,
  );
  const [importError, setImportError] = useState<string | null>(null);
  const chartKey = useRef(newProfileIdempotencyKey());
  const contactKey = useRef(newProfileIdempotencyKey());
  const prefill = useMemo(
    () => (imported === null ? undefined : registrationPrefill(imported.page)),
    [imported],
  );
  const importInput = useMemo(
    () => (imported === null ? null : chartImportInput(imported.page, kept)),
    [imported, kept],
  );
  const files = useMemo(
    () => (imported === null ? [] : keptFiles(imported.page, kept)),
    [imported, kept],
  );
  /** While the kept files are filed: which one, for the save button. */
  const [filing, setFiling] = useState<string | null>(null);
  const keptAddress =
    prefill?.address !== undefined && kept.has(CONTACT_KEYS.address) ? prefill.address : undefined;
  const keptContact =
    prefill?.emergencyContact !== undefined && kept.has(CONTACT_KEYS.emergencyContact)
      ? prefill.emergencyContact
      : undefined;
  const importCount =
    (importInput === null ? 0 : keptCount(importInput)) +
    files.length +
    (keptAddress === undefined ? 0 : 1) +
    (keptContact === undefined ? 0 : 1);
  const sourceLabel = emrImport === undefined ? "" : EMR_PAGE_SOURCE_LABELS[emrImport.source];
  const importing = imported !== null && importStage === "review";

  const busy = submitting || saving;
  const quick = quickAvailable && entry === "quick" && patient === null;

  const normalized = search.trim();
  const eligible = normalized.replace(/[^\p{L}\p{N}]/gu, "").length >= 4;
  useEffect(() => {
    const timer = setTimeout(() => setQuery(normalized), 200);
    return () => clearTimeout(timer);
  }, [normalized]);
  const matches = useQuery({
    queryKey: ["walk-in-patient-search", siteId, query],
    queryFn: ({ signal }) => client.searchWalkInPatients(siteId, query, signal),
    enabled: eligible && query === normalized && !patient,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const results = query === normalized && !matches.isError ? (matches.data ?? []).slice(0, 10) : [];
  const showResults = open && eligible && !patient && registeredWalkIn === null;

  /* The duplicate check: what was typed, once first name, last name and date of birth are valid. */
  const matchRequest = useMemo(
    () =>
      quick
        ? walkInChartMatchesRequest({
            legalFirstName: firstName,
            legalLastName: lastName,
            dateOfBirth,
            phone,
          })
        : null,
    [quick, firstName, lastName, dateOfBirth, phone],
  );
  const requestKey = matchRequest === null ? null : JSON.stringify(matchRequest);
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setCheckedKey(requestKey), MATCH_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [requestKey]);
  const fetchMatches = (request: WalkInChartMatchesRequest, signal?: AbortSignal) => {
    if (getWalkInChartMatches === undefined) throw new Error("The chart check is unavailable.");
    return getWalkInChartMatches(request, signal);
  };
  const chartMatches = useQuery<SchedulingPatientMatches>({
    queryKey: matchesKey(siteId, matchRequest),
    queryFn: ({ signal }) => {
      if (matchRequest === null) throw new Error("Nothing to check");
      return fetchMatches(matchRequest, signal);
    },
    enabled: matchRequest !== null && checkedKey === requestKey,
    // Each check is an audited read: once per typed set of details while the dialog is open.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });
  const listedMatches = matchRequest === null ? [] : (chartMatches.data?.matches ?? []);

  /* Changing who the details describe clears a chart picked for the earlier details. */
  const editIdentity = (set: (value: string) => void) => (value: string) => {
    set(value);
    setPicked(null);
    setNoneOfThese(false);
  };

  const choose = (match: WalkInPatientMatch) => {
    setPatient(match);
    setOpen(false);
    setActive(-1);
    setSaveError(null);
    // The messaging question is the quick form's: a chart chosen from the search has none on
    // screen, so an answer given there is not recorded on it (record it on the chart instead).
    setMessagingAnswer(null);
  };
  const close = () => {
    if (lock.current || submitting) return;
    // A walk-in registered from an import is on Today even when its entries were not saved.
    if (registeredWalkIn !== null) onAdded(registeredWalkIn);
    else onClose();
  };

  /**
   * Records the patient's answer about texts and emails on their chart (PA-8: a walk-in is always
   * on a chart, and a chart's decision covers their visits at this clinic). The visit is already
   * on Today; a refusal is shown with the code and never undoes the visit.
   */
  const recordMessagingAnswer = async (patientId: string, answer: BookingMessagingAnswer) => {
    if (recordPatientMessagingDecision === undefined) return;
    const what = answer.decision === "accepted" ? "agreed to" : "said no to";
    const common = {
      channel: "front_desk" as const,
      messageChannels: ["sms" as const, "email" as const],
      idempotencyKey: crypto.randomUUID(),
    };
    try {
      await recordPatientMessagingDecision(
        patientId,
        answer.decision === "accepted" && answer.documentVersion !== null
          ? { ...common, decision: "accepted", documentVersion: answer.documentVersion }
          : {
              ...common,
              decision: "declined",
              ...(answer.documentVersion === null
                ? {}
                : { documentVersion: answer.documentVersion }),
            },
      );
      setMessagingOutcome({
        recorded: true,
        text: `Recorded on the chart: the patient ${what} texts and emails.`,
      });
    } catch (caught) {
      const reason =
        messagingDecisionRefusalText(schedulingErrorDetail(caught)) ?? "It could not be saved.";
      setMessagingOutcome({
        recorded: false,
        text: `The patient's answer about texts and emails was not recorded. ${reason} Record it on the chart under Details, Consent.`,
      });
    }
  };

  const visitAdded = (result: StartPatientEncounterResult) => {
    codes?.onVisitAdded(result);
    setAdded({
      encounterId: result.patient.encounterId,
      displayName: result.patient.displayName,
      created: result.created,
    });
    if (messagingAnswer !== null) void recordMessagingAnswer(result.patient.id, messagingAnswer);
  };

  const startVisit = async (
    patientId: string,
    input: { chiefComplaint: string; urgency: WalkInUrgency },
  ) =>
    client.startPatientEncounter(patientId, {
      siteId,
      input,
      idempotencyKey: visitKey(JSON.stringify({ siteId, patientId, input })),
    });

  /** The chosen chart from the search, with or without a code. */
  const submitExisting = async (withCode: boolean) => {
    if (lock.current || !patient) return;
    const input = StartPatientEncounterInputSchema.safeParse({
      chiefComplaint: chiefComplaint.trim(),
      urgency,
    });
    if (!input.success) {
      setSaveError("Enter a reason for visit between 2 and 500 characters.");
      return;
    }
    lock.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await startVisit(patient.patientId, input.data);
      if (withCode && codes !== undefined) visitAdded(result);
      else onAdded(result);
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : "The patient could not be added.");
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  /** The quick path: reuse a listed chart, or make one, then show the code. */
  const submitQuick = async () => {
    if (lock.current || codes === undefined || createWalkIn === undefined) return;
    setSaveError(null);
    if (picked !== null) {
      const input = StartPatientEncounterInputSchema.safeParse({
        chiefComplaint: reason.trim(),
        urgency,
      });
      if (!input.success) {
        setSaveError("Enter a reason for visit between 2 and 500 characters.");
        return;
      }
      lock.current = true;
      setSaving(true);
      try {
        visitAdded(await startVisit(picked.patientId, input.data));
      } catch (caught) {
        setSaveError(caught instanceof Error ? caught.message : "The patient could not be added.");
      } finally {
        lock.current = false;
        setSaving(false);
      }
      return;
    }
    const registration = WalkInQuickRegistrationSchema.safeParse({
      legalFirstName: firstName,
      legalLastName: lastName,
      dateOfBirth,
      ...(sex === "" ? {} : { administrativeSex: sex }),
      reason,
      urgency,
      phone,
    });
    if (!registration.success) {
      setSaveError(registration.error.issues[0]?.message ?? "Check the patient's details.");
      return;
    }
    const request = walkInChartMatchesRequest({
      legalFirstName: registration.data.legalFirstName,
      legalLastName: registration.data.legalLastName,
      dateOfBirth: registration.data.dateOfBirth,
      phone: registration.data.phone,
    });
    if (request === null) return;
    lock.current = true;
    setSaving(true);
    try {
      // The duplicate check always runs for exactly these details before a chart is made.
      let existing: SchedulingPatientMatches;
      try {
        existing = await queryClient.fetchQuery({
          queryKey: matchesKey(siteId, request),
          queryFn: ({ signal }) => fetchMatches(request, signal),
          staleTime: Number.POSITIVE_INFINITY,
        });
      } catch {
        setSaveError(
          "The check for an existing chart could not be completed, so no chart was made. Try again.",
        );
        return;
      }
      setCheckedKey(JSON.stringify(request));
      if (existing.matches.length > 0 && !noneOfThese) {
        setSaveError(
          "This patient may already have a chart. Choose it above, or choose “None of these” to make a new chart.",
        );
        setFocusMatches(true);
        return;
      }
      const input = walkInQuickCreateInput(registration.data);
      visitAdded(
        await createWalkIn({
          siteId,
          idempotencyKey: walkInKey(JSON.stringify({ siteId, input })),
          input,
        }),
      );
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : "The patient could not be added.");
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  /** The import fills the full registration form, so from the quick path it opens that form. */
  const startImport = () => {
    setEntry("full");
    setImportStage("waiting");
  };
  const readChart = (chart: ReadChartPage) => {
    const fromPage = registrationPrefill(chart.page);
    setImported(chart);
    setKept(
      new Set([
        ...reviewItems(chart.page).map((item) => item.key),
        ...(fromPage.address === undefined ? [] : [CONTACT_KEYS.address]),
        ...(fromPage.emergencyContact === undefined ? [] : [CONTACT_KEYS.emergencyContact]),
      ]),
    );
    chartKey.current = newProfileIdempotencyKey();
    contactKey.current = newProfileIdempotencyKey();
    setFormVersion((version) => version + 1);
    setImportStage("review");
  };
  const discardImport = () => {
    setImported(null);
    setKept(new Set());
    setFormVersion((version) => version + 1);
    setImportStage("off");
  };

  /** Saves the kept entries, files the kept files, then saves the address and contact. */
  const saveImported = async (result: StartPatientEncounterResult) => {
    if (emrImport === undefined) return;
    const patientId = result.patient.id;
    if (importInput !== null && keptCount(importInput) > 0) {
      await emrImport.client.importEmrPageChart(patientId, {
        siteId,
        idempotencyKey: chartKey.current,
        input: importInput,
      });
    }
    if (files.length > 0) {
      try {
        await fileImportedFiles(
          emrImport.client,
          patientId,
          emrImport.source,
          files,
          (filed, total) =>
            setFiling(filed < total ? `Filing file ${filed + 1} of ${total}…` : null),
        );
      } finally {
        setFiling(null);
      }
    }
    if (keptAddress === undefined && keptContact === undefined) return;
    const profile = await emrImport.client.getPatientProfile(siteId, patientId);
    const current = profile.demographics;
    // A retry after a lost answer finds the details already saved and leaves them.
    const address =
      keptAddress !== undefined && !alreadyHolds(current.address, keptAddress)
        ? keptAddress
        : undefined;
    const emergencyContact =
      keptContact !== undefined && !alreadyHolds(current.emergencyContact, keptContact)
        ? keptContact
        : undefined;
    if (address === undefined && emergencyContact === undefined) return;
    await emrImport.client.updatePatientDemographics(patientId, {
      siteId,
      idempotencyKey: contactKey.current,
      demographics: {
        expectedVersion: current.version,
        ...(address === undefined ? {} : { address }),
        ...(emergencyContact === undefined ? {} : { emergencyContact }),
      },
    });
  };

  /** Registers the imported walk-in (once), then saves what was kept from the chart page. */
  const registerWithImport = async (input: WalkInIntakeInput | null) => {
    if (emrImport === undefined || lock.current) return;
    lock.current = true;
    setSaving(true);
    setImportError(null);
    let result = registeredWalkIn;
    try {
      if (result === null) {
        if (input === null) return;
        result = await emrImport.client.createWalkIn({
          siteId,
          idempotencyKey: walkInKey(JSON.stringify({ siteId, input })),
          input,
        });
        setRegisteredWalkIn(result);
      }
      if (importCount > 0) await saveImported(result);
      onAdded(result);
    } catch (caught) {
      const reason = caught instanceof Error ? caught.message : "It could not be saved.";
      setImportError(
        result === null
          ? reason
          : `${result.patient.displayName} is on Today, but not everything from ${sourceLabel} was saved: ${reason} Save it again, or close without it.`,
      );
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  // Once the form is enabled again, take the desk to the charts to choose from.
  useEffect(() => {
    if (!focusMatches || saving) return;
    setFocusMatches(false);
    matchesRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [focusMatches, saving]);

  /**
   * Enter moves through the quick fields and submits from the last one (the reason). The Sex and
   * Urgency lists take part too: Enter on a closed list moves on instead of opening it or
   * submitting the form early (implicit submission).
   */
  const quickFieldKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    const form = event.currentTarget.form;
    if (form === null) return;
    if (event.currentTarget.dataset.lastField !== undefined) {
      form.requestSubmit();
      return;
    }
    const fields = Array.from(
      form.querySelectorAll<HTMLElement>("input:not([type='radio']), select"),
    ).filter((field) => !field.hasAttribute("disabled"));
    fields[fields.indexOf(event.currentTarget) + 1]?.focus();
  };

  if (added !== null) {
    return (
      <ScheduleModal
        title="Intake code"
        className="tb-walk-in-modal"
        description={
          added.created
            ? `${added.displayName} is on Today.`
            : `${added.displayName} already had an open visit; it was kept.`
        }
        onClose={onClose}
      >
        <WalkInIntakeCode
          encounterId={added.encounterId}
          patientName={added.displayName}
          visitJustAdded
          onDone={onClose}
          onReviewIntake={codes?.onReviewIntake}
        />
        {messagingOutcome !== null ? (
          <p
            className={messagingOutcome.recorded ? "schedule-modal-note" : "schedule-modal-error"}
            role={messagingOutcome.recorded ? "status" : "alert"}
          >
            {messagingOutcome.text}
          </p>
        ) : null}
      </ScheduleModal>
    );
  }

  const matchesPanel = (() => {
    if (!quick || matchRequest === null) return null;
    if (checkedKey !== requestKey || chartMatches.isPending) {
      return (
        <p className="schedule-modal-note wide tb-walk-in-check" role="status">
          Checking for an existing chart…
        </p>
      );
    }
    if (chartMatches.isError) {
      return (
        <p className="schedule-modal-error wide" role="alert">
          <span>The check for an existing chart could not be completed.</span>{" "}
          <button
            type="button"
            className="schedule-btn ghost sm"
            onClick={() => void chartMatches.refetch()}
          >
            Check again
          </button>
        </p>
      );
    }
    if (listedMatches.length === 0) {
      return (
        <p className="schedule-modal-note wide tb-walk-in-check" role="status">
          No existing chart matches these details.
        </p>
      );
    }
    return (
      <div
        ref={matchesRef}
        className="tb-walk-in-matches wide"
        role="radiogroup"
        aria-labelledby={matchesTitleId}
      >
        <p id={matchesTitleId} className="tb-walk-in-matches-title">
          {listedMatches.length === 1
            ? "This patient may already have a chart"
            : `This patient may already have one of ${listedMatches.length} charts`}
        </p>
        {listedMatches.map((match) => (
          <label
            key={match.patientId}
            className={`tb-walk-in-match${picked?.patientId === match.patientId ? " selected" : ""}`}
          >
            <input
              type="radio"
              name={matchesName}
              checked={picked?.patientId === match.patientId}
              onChange={() => {
                setPicked(match);
                setNoneOfThese(false);
                setSaveError(null);
              }}
            />
            <span>
              <strong>Use the chart for {match.displayName}</strong>
              <small>
                DOB {match.dateOfBirth} · MRN {match.mrn} · same{" "}
                {match.matchedOn.map((signal) => MATCH_SIGNALS[signal]).join(", ")}
              </small>
              {match.lastVisitAt ? (
                <small>Last visit {new Date(match.lastVisitAt).toLocaleDateString()}</small>
              ) : null}
            </span>
          </label>
        ))}
        <label className={`tb-walk-in-match${noneOfThese ? " selected" : ""}`}>
          <input
            type="radio"
            name={matchesName}
            checked={noneOfThese}
            onChange={() => {
              setPicked(null);
              setNoneOfThese(true);
              setSaveError(null);
            }}
          />
          <span>
            <strong>None of these: make a new chart</strong>
          </span>
        </label>
      </div>
    );
  })();

  const footer = (
    <div className="schedule-modal-actions">
      <button type="button" className="schedule-btn ghost" disabled={busy} onClick={close}>
        {registeredWalkIn !== null ? "Close without them" : "Cancel"}
      </button>
      {registeredWalkIn !== null ? (
        <button
          type="button"
          className="schedule-btn primary"
          disabled={busy}
          onClick={() => void registerWithImport(null)}
        >
          {saving ? (filing ?? "Saving entries…") : "Save the imported entries"}
        </button>
      ) : patient ? (
        <>
          <button
            type="submit"
            form={existingPatientFormId}
            className={`schedule-btn ${codes === undefined ? "primary" : "ghost"}`}
            disabled={busy}
          >
            {saving && codes === undefined ? "Adding patient…" : "Add to Today"}
          </button>
          {codes !== undefined ? (
            <button
              type="button"
              className="schedule-btn primary"
              disabled={busy}
              onClick={() => void submitExisting(true)}
            >
              {saving ? "Adding patient…" : "Add and show code"}
            </button>
          ) : null}
        </>
      ) : quick ? (
        <button type="submit" form={quickFormId} className="schedule-btn primary" disabled={busy}>
          {saving ? "Adding patient…" : "Add and show code"}
        </button>
      ) : (
        <button
          type="submit"
          form={newPatientFormId}
          className="schedule-btn primary"
          disabled={busy}
        >
          {busy ? (filing ?? "Registering and adding…") : "Register and add to Today"}
        </button>
      )}
    </div>
  );

  return (
    <ScheduleModal
      title="Add walk-in"
      className="tb-walk-in-modal"
      onClose={close}
      onEscape={() => {
        // Escape first closes the open list of matching patients, as in any combobox.
        if (!showResults) return false;
        setOpen(false);
        return true;
      }}
    >
      <div className="tb-existing-search">
        <label htmlFor={`${listId}-input`}>Add existing patient</label>
        <input
          id={`${listId}-input`}
          // With phone codes the dialog opens in the quick form (Legal first name) instead.
          data-autofocus={quickAvailable ? undefined : true}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showResults && results.length > 0}
          aria-controls={listId}
          aria-activedescendant={
            showResults && active >= 0 && results[active] ? `${listId}-${active}` : undefined
          }
          aria-describedby={`${listId}-hint`}
          placeholder="Name, date of birth, health card or phone"
          maxLength={120}
          value={search}
          disabled={busy || registeredWalkIn !== null}
          onChange={(event) => {
            setSearch(event.target.value);
            setPatient(null);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(event) => {
            if ((event.key === "ArrowDown" || event.key === "ArrowUp") && results.length) {
              event.preventDefault();
              setOpen(true);
              setActive((index) =>
                event.key === "ArrowDown"
                  ? (index + 1) % results.length
                  : index <= 0
                    ? results.length - 1
                    : index - 1,
              );
            }
            if (event.key === "Enter" && showResults && active >= 0 && results[active]) {
              event.preventDefault();
              choose(results[active]);
            }
          }}
        />
        <p id={`${listId}-hint`}>
          Type at least 4 letters or numbers. Combine details to narrow matches; newest visits
          appear first.
        </p>
        {showResults ? (
          <div className="tb-patient-dropdown">
            {query !== normalized || matches.isPending ? (
              <p role="status">Searching patients…</p>
            ) : matches.isError ? (
              <p role="alert">
                Patient search failed.{" "}
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void matches.refetch()}
                >
                  Retry search
                </button>
              </p>
            ) : results.length === 0 ? (
              <p role="status">
                No matching patients. Try another detail or {quick ? "add" : "register"} a new
                patient below.
              </p>
            ) : null}
            <div id={listId} role="listbox" aria-label="Matching patients">
              {results.map((entry, index) => (
                <div
                  key={entry.patientId}
                  id={`${listId}-${index}`}
                  role="option"
                  tabIndex={-1}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      choose(entry);
                    }
                  }}
                  aria-selected={index === active}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(entry)}
                >
                  <strong>{entry.displayName}</strong>
                  <span>
                    DOB {entry.dateOfBirth} · MRN {entry.mrn}
                  </span>
                  <span>
                    {entry.phone ? `Phone ${entry.phone}` : "No phone recorded"}
                    {entry.healthCardLast4 ? ` · Health card •••• ${entry.healthCardLast4}` : ""}
                  </span>
                  <small>
                    {entry.lastVisitAt
                      ? `Last visit ${new Date(entry.lastVisitAt).toLocaleDateString()}`
                      : "No previous visit"}
                  </small>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {patient ? (
        <form
          id={existingPatientFormId}
          className="tb-walk-in-section"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void submitExisting(false);
          }}
        >
          <fieldset disabled={busy}>
            <legend className="tb-walk-in-legend">Visit for {patient.displayName}</legend>
            <p className="schedule-modal-note">
              DOB {patient.dateOfBirth} · MRN {patient.mrn}
            </p>
            <button
              type="button"
              className="schedule-btn ghost sm"
              onClick={() => {
                setPatient(null);
                setSearch("");
                setSaveError(null);
              }}
            >
              Choose another patient or register new
            </button>
            <div className="schedule-modal-fields">
              <label className="schedule-modal-field wide">
                Chief complaint
                <textarea
                  required
                  minLength={2}
                  maxLength={500}
                  rows={2}
                  value={chiefComplaint}
                  onChange={(event) => setChiefComplaint(event.target.value)}
                />
              </label>
              <label className="schedule-modal-field">
                Urgency
                <select
                  value={urgency}
                  onChange={(event) => setUrgency(event.target.value as WalkInUrgency)}
                >
                  <option value="routine">Routine</option>
                  <option value="soon">Soon</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
            </div>
          </fieldset>
          {saveError ? (
            <p role="alert" className="schedule-modal-error">
              {saveError}
            </p>
          ) : null}
        </form>
      ) : quick ? (
        <form
          id={quickFormId}
          className="tb-walk-in-section"
          noValidate
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void submitQuick();
          }}
        >
          <fieldset disabled={busy}>
            <legend className="tb-walk-in-legend">Add a walk-in</legend>
            <p className="schedule-modal-note">
              The patient answers the rest of the questions on their phone from the code.
            </p>
            <div className="schedule-modal-fields">
              <label className="schedule-modal-field">
                Legal first name
                <input
                  required
                  autoComplete="off"
                  maxLength={100}
                  data-autofocus
                  value={firstName}
                  onChange={(event) => editIdentity(setFirstName)(event.target.value)}
                  onKeyDown={quickFieldKeyDown}
                />
              </label>
              <label className="schedule-modal-field">
                Legal last name
                <input
                  required
                  autoComplete="off"
                  maxLength={100}
                  value={lastName}
                  onChange={(event) => editIdentity(setLastName)(event.target.value)}
                  onKeyDown={quickFieldKeyDown}
                />
              </label>
              <label className="schedule-modal-field">
                Date of birth
                <input
                  required
                  type="date"
                  value={dateOfBirth}
                  onChange={(event) => editIdentity(setDateOfBirth)(event.target.value)}
                  onKeyDown={quickFieldKeyDown}
                />
              </label>
              {picked === null ? (
                <label className="schedule-modal-field">
                  Sex
                  <select
                    required
                    value={sex}
                    onChange={(event) => setSex(event.target.value)}
                    onKeyDown={quickFieldKeyDown}
                  >
                    <option value="" disabled>
                      Choose…
                    </option>
                    {SEX_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {matchesPanel}
              {picked === null ? (
                <label className="schedule-modal-field">
                  Mobile number (optional)
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    maxLength={40}
                    placeholder="For texting the link later"
                    value={phone}
                    onChange={(event) => editIdentity(setPhone)(event.target.value)}
                    onKeyDown={quickFieldKeyDown}
                  />
                </label>
              ) : (
                <p className="schedule-modal-note wide">
                  Using {picked.displayName}’s chart (MRN {picked.mrn}). Its details stay as they
                  are; the patient confirms coverage and health answers on their phone.
                </p>
              )}
              <label className="schedule-modal-field">
                Urgency
                <select
                  value={urgency}
                  onChange={(event) => setUrgency(event.target.value as WalkInUrgency)}
                  onKeyDown={quickFieldKeyDown}
                >
                  <option value="routine">Routine</option>
                  <option value="soon">Soon</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              {getPatientConsentDocuments !== undefined &&
              recordPatientMessagingDecision !== undefined ? (
                // Full width, without the field label's small-caps type (the control has its own).
                <div className="wide">
                  <BookingMessagingField
                    siteId={siteId}
                    client={{ getPatientConsentDocuments }}
                    answer={messagingAnswer}
                    onAnswer={setMessagingAnswer}
                  />
                </div>
              ) : null}
              <label className="schedule-modal-field wide">
                Reason for visit
                <input
                  required
                  autoComplete="off"
                  maxLength={500}
                  data-last-field
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  onKeyDown={quickFieldKeyDown}
                />
              </label>
            </div>
          </fieldset>
          <button type="button" className="tb-walk-in-switch" onClick={() => setEntry("full")}>
            Enter all details now
          </button>
          {emrImport !== undefined ? (
            <button type="button" className="tb-walk-in-switch" onClick={startImport}>
              Import from {sourceLabel}
            </button>
          ) : null}
          {saveError ? (
            <p role="alert" className="schedule-modal-error">
              {saveError}
            </p>
          ) : null}
        </form>
      ) : (
        <>
          {quickAvailable && importStage === "off" ? (
            <button type="button" className="tb-walk-in-switch" onClick={() => setEntry("quick")}>
              Back to the quick add and phone code
            </button>
          ) : null}
          {/* The --pp-* tokens and field styles for the registration form and the import. */}
          <div className="pp-section pp-modal-body">
            {emrImport !== undefined && importStage === "off" ? (
              <div className="pp-walk-in-import">
                <button
                  type="button"
                  className="pp-btn ghost sm"
                  onClick={() => setImportStage("waiting")}
                >
                  Import from {sourceLabel}
                </button>
              </div>
            ) : null}
            {emrImport !== undefined && importStage === "waiting" ? (
              <EmrImportPanel
                source={emrImport.source}
                onRead={readChart}
                onCancel={() => setImportStage("off")}
              />
            ) : null}
            {importing ? (
              <EmrImportBanner
                page={imported.page}
                capturedAt={imported.capturedAt}
                onDiscard={discardImport}
                disabled={busy || registeredWalkIn !== null}
              />
            ) : null}
            {importError !== null ? (
              <p role="alert" className="pp-error-block">
                {importError}
              </p>
            ) : null}
            <WalkInForm
              key={formVersion}
              formId={newPatientFormId}
              embedded
              submitting={busy}
              disabled={registeredWalkIn !== null}
              error={error}
              {...(prefill === undefined ? {} : { initial: prefill })}
              onSubmit={importing ? (input) => void registerWithImport(input) : onSubmit}
              onCancel={close}
              healthCardPhotos={importing ? undefined : healthCardPhotos}
            />
            {importing ? (
              <div className="pp-form">
                <EmrImportChartReview
                  page={imported.page}
                  kept={kept}
                  onKeptChange={setKept}
                  disabled={busy || registeredWalkIn !== null}
                  extra={
                    prefill?.address !== undefined || prefill?.emergencyContact !== undefined ? (
                      <div className="pp-import-group">
                        <p className="pp-import-group-head">Saved to the chart after registering</p>
                        <ul className="pp-import-items">
                          {prefill?.address !== undefined ? (
                            <li>
                              <label className="pp-import-item">
                                <input
                                  type="checkbox"
                                  checked={kept.has(CONTACT_KEYS.address)}
                                  onChange={(event) => {
                                    const next = new Set(kept);
                                    if (event.target.checked) next.add(CONTACT_KEYS.address);
                                    else next.delete(CONTACT_KEYS.address);
                                    setKept(next);
                                  }}
                                />
                                <span className="pp-import-item-text">
                                  <span className="pp-import-item-title">Address</span>
                                  <span className="pp-quiet">{addressLine(prefill.address)}</span>
                                </span>
                              </label>
                            </li>
                          ) : null}
                          {prefill?.emergencyContact !== undefined ? (
                            <li>
                              <label className="pp-import-item">
                                <input
                                  type="checkbox"
                                  checked={kept.has(CONTACT_KEYS.emergencyContact)}
                                  onChange={(event) => {
                                    const next = new Set(kept);
                                    if (event.target.checked)
                                      next.add(CONTACT_KEYS.emergencyContact);
                                    else next.delete(CONTACT_KEYS.emergencyContact);
                                    setKept(next);
                                  }}
                                />
                                <span className="pp-import-item-text">
                                  <span className="pp-import-item-title">Emergency contact</span>
                                  <span className="pp-quiet">
                                    {contactLine(prefill.emergencyContact)}
                                  </span>
                                </span>
                              </label>
                            </li>
                          ) : null}
                        </ul>
                      </div>
                    ) : null
                  }
                />
                <EmrImportLeftOut page={imported.page} />
              </div>
            ) : null}
            {importing && registeredWalkIn === null && importCount > 0 ? (
              <p className="pp-quiet">
                {importCount} {importCount === 1 ? "entry" : "entries"} from {sourceLabel} will be
                saved to the new chart.
              </p>
            ) : null}
          </div>
        </>
      )}
      {footer}
    </ScheduleModal>
  );
}
